import React, { useMemo, useState, useEffect } from "react";
import { useGetCentres } from "../../api/settings";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  ListSubheader,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";

const DropdownIcon = (props) => (
  <Icon icon="eva:arrow-ios-downward-fill" width={18} height={18} {...props} />
);

function HubFilter({ hubIds = [], onChange }) {
  // Pagination states
  const [page, setPage] = useState(1);
  const limit = 50;
  const [allCentres, setAllCentres] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const {
    centres = [],
    isLoading,
    pagination,
  } = useGetCentres({
    centerType: "hub",
    onlyActive: "active",
    page,
    limit,
  });

  const [search, setSearch] = useState("");

  // Append paginated centres
  useEffect(() => {
    if (!isLoading && Array.isArray(centres)) {
      setAllCentres((prev) => {
        if (page === 1) return [...centres];

        const existingIds = new Set(prev.map((item) => item.id));

        const newItems = centres.filter((item) => !existingIds.has(item.id));

        return [...prev, ...newItems];
      });

      setLoadingMore(false);
    }
  }, [centres, isLoading, page]);

  // Infinite scroll
  const handleScroll = (e) => {
    const target = e.currentTarget;

    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    const hasMore = page < (pagination?.totalPages || 1);

    if (isNearBottom && hasMore && !loadingMore) {
      setLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  };

  // Hub options
  const hubOptions = useMemo(
    () =>
      allCentres.map((h) => ({
        label: h.name?.trim(),
        value: h.id,
      })),
    [allCentres],
  );

  // Search filter
  const filteredHubs = useMemo(() => {
    return hubOptions.filter((hub) =>
      hub.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [hubOptions, search]);

  return (
    <Box>
      <FormControl fullWidth size="small">
        <Select
          displayEmpty
          value={hubIds[0] || ""}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Box sx={{ color: "var(--app-text-muted)" }}>
                  Select Hub Centre
                </Box>
              );
            }

            const selectedHub = hubOptions.find(
              (hub) => hub.value === selected,
            );

            return selectedHub?.label || "";
          }}
          onChange={(e) => onChange([e.target.value])}
          IconComponent={DropdownIcon}
          onOpen={() => setSearch("")}
          sx={{
            height: "48px",
            borderRadius: "10px",
            backgroundColor: "var(--app-surface)",

            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--app-border)",
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--app-border)",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--app-border)",
            },

            "& .MuiSelect-select": {
              padding: "12px 14px",
              color: hubIds.length
                ? "var(--app-text)"
                : "var(--app-text-muted)",
            },
          }}
          MenuProps={{
            autoFocus: false,
            disableAutoFocusItem: true,
            PaperProps: {
              onScroll: handleScroll,
              sx: {
                mt: 1,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: "12px",
                border: "1px solid var(--app-border)",
              },
            },
          }}
        >
          {/* Search field */}
          <ListSubheader
            onClick={(e) => e.stopPropagation()}
            sx={{ backgroundColor: "var(--app-surface)" }}
          >
            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder="Search hub..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "12px",

                  "& fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&:hover fieldset": {
                    borderColor: "var(--app-border)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: "var(--app-border)",
                    borderWidth: "1px",
                  },

                  "&.Mui-focused": {
                    boxShadow: "none",
                  },
                },
              }} 
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "var(--app-text-muted)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>

          {/* Initial Loader */}
          {isLoading && page === 1 ? (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          ) : filteredHubs.length > 0 ? (
            filteredHubs.map((hub) => (
              <MenuItem key={hub.value} value={hub.value}>
                {hub.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No results found</MenuItem>
          )}

          {/* Bottom lazy loader */}
          {loadingMore && (
            <MenuItem disabled>
              <CircularProgress size={18} />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
}

export default HubFilter;
